import { useState, useEffect } from 'react'
import { personaAPI } from '../../services/api'
import PersonaPreview from './PersonaPreview'

// Helper: multi-select badge chip
function BadgeSelect({ options, selected, onChange, disabled }) {
  const toggle = (val) => {
    if (disabled) return
    const next = selected.includes(val)
      ? selected.filter((v) => v !== val)
      : [...selected, val]
    onChange(next)
  }
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          className={`badge cursor-pointer select-none ${selected.includes(opt) ? 'badge-primary' : 'badge-outline'}`}
          onClick={() => toggle(opt)}
          disabled={disabled}
        >
          {opt}
        </button>
      ))}
    </div>
  )
}

// Helper: dropdown
function Dropdown({ label, options, value, onChange, disabled, required }) {
  return (
    <div className="form-control">
      <label className="label">
        <span className="label-text font-semibold">
          {label} {required && <span className="text-error">*</span>}
        </span>
      </label>
      <select
        className="select select-bordered select-sm w-full"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
      >
        <option value="">Select...</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </div>
  )
}

// Helper: probability slider
function ProbSlider({ label, value, onChange, disabled }) {
  return (
    <div className="form-control">
      <label className="label">
        <span className="label-text text-sm">{label}</span>
        <span className="label-text-alt">{Math.round(value * 100)}%</span>
      </label>
      <input
        type="range"
        min={0}
        max={100}
        value={Math.round(value * 100)}
        onChange={(e) => onChange(parseInt(e.target.value) / 100)}
        className="range range-primary range-xs"
        disabled={disabled}
      />
    </div>
  )
}

// Helper: toggle
function Toggle({ label, checked, onChange, disabled }) {
  return (
    <label className="label cursor-pointer justify-start gap-3">
      <input
        type="checkbox"
        className="toggle toggle-primary toggle-sm"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        disabled={disabled}
      />
      <span className="label-text text-sm">{label}</span>
    </label>
  )
}

// Collapse panel wrapper using native details/summary for reliable behavior
function Panel({ title, defaultOpen, children }) {
  return (
    <details className="border border-base-300 rounded-box" open={defaultOpen || undefined}>
      <summary className="px-4 py-3 cursor-pointer font-medium text-sm bg-base-200 rounded-box hover:bg-base-300 transition-colors select-none">
        {title}
      </summary>
      <div className="p-4 pt-2">
        {children}
      </div>
    </details>
  )
}

export default function PersonaGenerator({ onPersonaCreated }) {
  const [dimensions, setDimensions] = useState(null)
  const [generating, setGenerating] = useState(false)
  const [generatedPersona, setGeneratedPersona] = useState(null)
  const [error, setError] = useState(null)

  // Panel 0: Clinical Core (required)
  const [selectedType, setSelectedType] = useState('')
  const [selectedCause, setSelectedCause] = useState('')
  const [selectedTreatment, setSelectedTreatment] = useState('')

  // Panel 1: Demographics
  const [ageRange, setAgeRange] = useState('')
  const [gender, setGender] = useState('')
  const [occupation, setOccupation] = useState('')
  const [socioeconomicStatus, setSocioeconomicStatus] = useState('')

  // Panel 2: Clinical Profile
  const [comorbidConditions, setComorbidConditions] = useState([])
  const [symptomDuration, setSymptomDuration] = useState('')
  const [priorTreatments, setPriorTreatments] = useState([])

  // Panel 3: Contextual Factors
  const [familySituation, setFamilySituation] = useState('')
  const [workSchedule, setWorkSchedule] = useState('')
  const [caregivingResponsibilities, setCaregivingResponsibilities] = useState('')
  const [accessToCare, setAccessToCare] = useState('')
  const [insuranceFinancial, setInsuranceFinancial] = useState('')
  const [technologyComfort, setTechnologyComfort] = useState('')
  const [trustInClinicians, setTrustInClinicians] = useState('')

  // Panel 4: Archetype
  const [archetype, setArchetype] = useState('')

  // Panel 5: Environmental Factors
  const [adrProbability, setAdrProbability] = useState(0.2)
  const [treatmentComplicationProbability, setTreatmentComplicationProbability] = useState(0.15)
  const [lifeEvents, setLifeEvents] = useState({
    major_illness_in_family: false,
    financial_stress: false,
    loss_of_loved_one: false,
    housing_change: false,
  })
  const [systemBarriers, setSystemBarriers] = useState({
    insurance_denial: false,
    provider_turnover: false,
    appointment_delays: false,
  })

  // Panel 6: Agent Choices
  const [medicationAdherence, setMedicationAdherence] = useState(0.7)
  const [therapyCompliance, setTherapyCompliance] = useState(0.6)
  const [lifestyleChange, setLifestyleChange] = useState(0.5)

  useEffect(() => {
    personaAPI.getDimensions()
      .then(setDimensions)
      .catch(() => setError('Failed to load dimensions'))
  }, [])

  // Auto-fill Panels 5 & 6 when archetype changes
  useEffect(() => {
    if (!archetype || !dimensions?.archetype_presets?.[archetype]) return
    const preset = dimensions.archetype_presets[archetype]
    const env = preset.environmental_factors || {}
    const choices = preset.agent_choices || {}

    setAdrProbability(env.adr_probability ?? 0.2)
    setTreatmentComplicationProbability(env.treatment_complication_probability ?? 0.15)
    setLifeEvents(env.life_events ?? { major_illness_in_family: false, financial_stress: false, loss_of_loved_one: false, housing_change: false })
    setSystemBarriers(env.system_barriers ?? { insurance_denial: false, provider_turnover: false, appointment_delays: false })
    setMedicationAdherence(choices.medication_adherence_probability ?? 0.7)
    setTherapyCompliance(choices.therapy_compliance_probability ?? 0.6)
    setLifestyleChange(choices.lifestyle_change_probability ?? 0.5)
  }, [archetype, dimensions])

  const handleGenerate = async () => {
    if (!selectedType || !selectedCause || !selectedTreatment) return
    setGenerating(true)
    setError(null)
    setGeneratedPersona(null)

    // Build form data object
    const formData = {
      sleepDisorderType: selectedType,
      primaryCause: selectedCause,
      treatmentApproach: selectedTreatment,
    }

    // Add optional fields only if any value is set
    if (ageRange || gender || occupation || socioeconomicStatus) {
      formData.demographics = { ageRange, gender, occupation, socioeconomicStatus }
    }
    if (comorbidConditions.length || symptomDuration || priorTreatments.length) {
      formData.clinicalAdditions = { comorbidConditions, symptomDuration, priorTreatments }
    }
    if (familySituation || workSchedule || caregivingResponsibilities || accessToCare || insuranceFinancial || technologyComfort || trustInClinicians) {
      formData.contextualFactors = { familySituation, workSchedule, caregivingResponsibilities, accessToCare, insuranceFinancial, technologyComfort, trustInClinicians }
    }
    if (archetype) {
      formData.archetype = archetype
      formData.environmentalFactors = {
        adrProbability: adrProbability,
        treatmentComplicationProbability: treatmentComplicationProbability,
        lifeEvents,
        systemBarriers,
      }
      formData.agentChoices = {
        medicationAdherenceProbability: medicationAdherence,
        therapyComplianceProbability: therapyCompliance,
        lifestyleChangeProbability: lifestyleChange,
      }
    }

    try {
      const persona = await personaAPI.generate(formData)
      setGeneratedPersona(persona)
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate persona')
    } finally {
      setGenerating(false)
    }
  }

  const canGenerate = selectedType && selectedCause && selectedTreatment && !generating

  if (!dimensions) {
    return (
      <div className="flex justify-center py-12">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    )
  }

  const ext = dimensions.extended || {}
  const presets = dimensions.archetype_presets || {}

  return (
    <div className="space-y-4">
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title text-xl mb-2">Create Patient Persona</h2>
          <p className="text-base-content/70 mb-4 text-sm">
            Select core clinical dimensions (required) and optionally expand panels below for richer personas.
          </p>

          {/* Panel 0: Clinical Core - always visible */}
          <div className="bg-base-200 rounded-box p-4 mb-2">
            <h3 className="font-semibold mb-3">Clinical Core <span className="text-error text-xs">(Required)</span></h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Dropdown
                label="Sleep Disorder Type"
                options={dimensions.Types}
                value={selectedType}
                onChange={setSelectedType}
                disabled={generating}
                required
              />
              <Dropdown
                label="Primary Cause"
                options={dimensions.Causes}
                value={selectedCause}
                onChange={setSelectedCause}
                disabled={generating}
                required
              />
              <Dropdown
                label="Treatment Approach"
                options={dimensions.Treatments}
                value={selectedTreatment}
                onChange={setSelectedTreatment}
                disabled={generating}
                required
              />
            </div>
          </div>

          {/* Panel 1: Demographics */}
          <Panel title="Demographics (Optional)">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Dropdown label="Age Range" options={ext.demographics?.age_ranges || []} value={ageRange} onChange={setAgeRange} disabled={generating} />
              <Dropdown label="Gender" options={ext.demographics?.genders || []} value={gender} onChange={setGender} disabled={generating} />
              <Dropdown label="Occupation" options={ext.demographics?.occupations || []} value={occupation} onChange={setOccupation} disabled={generating} />
              <Dropdown label="Socioeconomic Status" options={ext.demographics?.socioeconomic_statuses || []} value={socioeconomicStatus} onChange={setSocioeconomicStatus} disabled={generating} />
            </div>
          </Panel>

          {/* Panel 2: Clinical Profile */}
          <Panel title="Clinical Profile (Optional)">
            <div className="space-y-4">
              <div>
                <label className="label"><span className="label-text font-semibold">Comorbid Conditions</span></label>
                <BadgeSelect
                  options={ext.clinical_additions?.comorbid_conditions || []}
                  selected={comorbidConditions}
                  onChange={setComorbidConditions}
                  disabled={generating}
                />
              </div>
              <Dropdown label="Symptom Duration" options={ext.clinical_additions?.symptom_durations || []} value={symptomDuration} onChange={setSymptomDuration} disabled={generating} />
              <div>
                <label className="label"><span className="label-text font-semibold">Prior Treatments</span></label>
                <BadgeSelect
                  options={ext.clinical_additions?.prior_treatments || []}
                  selected={priorTreatments}
                  onChange={setPriorTreatments}
                  disabled={generating}
                />
              </div>
            </div>
          </Panel>

          {/* Panel 3: Contextual Factors */}
          <Panel title="Contextual Factors (Optional)">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Dropdown label="Family Situation" options={ext.contextual_factors?.family_situations || []} value={familySituation} onChange={setFamilySituation} disabled={generating} />
              <Dropdown label="Work Schedule" options={ext.contextual_factors?.work_schedules || []} value={workSchedule} onChange={setWorkSchedule} disabled={generating} />
              <Dropdown label="Caregiving Responsibilities" options={ext.contextual_factors?.caregiving_responsibilities || []} value={caregivingResponsibilities} onChange={setCaregivingResponsibilities} disabled={generating} />
              <Dropdown label="Access to Care" options={ext.contextual_factors?.access_to_care || []} value={accessToCare} onChange={setAccessToCare} disabled={generating} />
              <Dropdown label="Insurance / Financial" options={ext.contextual_factors?.insurance_financial || []} value={insuranceFinancial} onChange={setInsuranceFinancial} disabled={generating} />
              <Dropdown label="Technology Comfort" options={ext.contextual_factors?.technology_comfort || []} value={technologyComfort} onChange={setTechnologyComfort} disabled={generating} />
              <Dropdown label="Trust in Clinicians" options={ext.contextual_factors?.trust_in_clinicians || []} value={trustInClinicians} onChange={setTrustInClinicians} disabled={generating} />
            </div>
          </Panel>

          {/* Panel 4: Patient Archetype */}
          <Panel title="Patient Archetype (Optional)">
            <Dropdown
              label="Archetype"
              options={ext.archetypes || []}
              value={archetype}
              onChange={setArchetype}
              disabled={generating}
            />
            {archetype && presets[archetype] && (
              <div className="alert alert-info mt-3 text-sm">
                <span>{presets[archetype].description}</span>
              </div>
            )}
          </Panel>

          {/* Panel 5: Environmental Factors */}
          <Panel title="Environmental Factors (Optional)">
            <div className="space-y-3">
              <ProbSlider label="ADR Probability" value={adrProbability} onChange={setAdrProbability} disabled={generating} />
              <ProbSlider label="Treatment Complication Probability" value={treatmentComplicationProbability} onChange={setTreatmentComplicationProbability} disabled={generating} />
              <div className="divider text-xs">Life Events</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                <Toggle label="Major illness in family" checked={lifeEvents.major_illness_in_family} onChange={(v) => setLifeEvents({ ...lifeEvents, major_illness_in_family: v })} disabled={generating} />
                <Toggle label="Financial stress" checked={lifeEvents.financial_stress} onChange={(v) => setLifeEvents({ ...lifeEvents, financial_stress: v })} disabled={generating} />
                <Toggle label="Loss of loved one" checked={lifeEvents.loss_of_loved_one} onChange={(v) => setLifeEvents({ ...lifeEvents, loss_of_loved_one: v })} disabled={generating} />
                <Toggle label="Housing change" checked={lifeEvents.housing_change} onChange={(v) => setLifeEvents({ ...lifeEvents, housing_change: v })} disabled={generating} />
              </div>
              <div className="divider text-xs">System Barriers</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                <Toggle label="Insurance denial" checked={systemBarriers.insurance_denial} onChange={(v) => setSystemBarriers({ ...systemBarriers, insurance_denial: v })} disabled={generating} />
                <Toggle label="Provider turnover" checked={systemBarriers.provider_turnover} onChange={(v) => setSystemBarriers({ ...systemBarriers, provider_turnover: v })} disabled={generating} />
                <Toggle label="Appointment delays" checked={systemBarriers.appointment_delays} onChange={(v) => setSystemBarriers({ ...systemBarriers, appointment_delays: v })} disabled={generating} />
              </div>
            </div>
          </Panel>

          {/* Panel 6: Agent Choices */}
          <Panel title="Agent Choices (Optional)">
            <div className="space-y-3">
              <ProbSlider label="Medication Adherence" value={medicationAdherence} onChange={setMedicationAdherence} disabled={generating} />
              <ProbSlider label="Therapy Compliance" value={therapyCompliance} onChange={setTherapyCompliance} disabled={generating} />
              <ProbSlider label="Lifestyle Changes" value={lifestyleChange} onChange={setLifestyleChange} disabled={generating} />
            </div>
          </Panel>

          {error && (
            <div className="alert alert-error mt-4">
              <span>{error}</span>
            </div>
          )}

          <div className="card-actions justify-end mt-4">
            <button
              className={`btn btn-primary ${generating ? 'loading' : ''}`}
              onClick={handleGenerate}
              disabled={!canGenerate}
            >
              {generating ? 'Generating...' : 'Generate Persona'}
            </button>
          </div>
        </div>
      </div>

      {generating && (
        <div className="flex flex-col items-center py-8 gap-4">
          <span className="loading loading-dots loading-lg text-primary"></span>
          <p className="text-base-content/70">Generating patient persona with OpenAI... This may take 10-15 seconds.</p>
        </div>
      )}

      {generatedPersona && (
        <PersonaPreview
          persona={generatedPersona}
          onUseForJourney={() => onPersonaCreated?.(generatedPersona.id)}
        />
      )}
    </div>
  )
}
